/* The two small facts about a service that everything else draws from: its
 * hotkey, and the label that goes in the square.
 *
 * ── WHY THIS IS A RULES FILE ─────────────────────────────────────────────────
 *
 * The hotkey is OPTIONAL. A salon that books with the mouse, or from the phone,
 * or off the lobby tablet never presses one, and a key assigned on its behalf is
 * not a convenience — it is a collision. Two services beginning with P both
 * silently claiming `P`, one of them answering and the other never, is a bug
 * nobody reports because nothing looks wrong.
 *
 * But "no key" has a consequence that reaches every square on the board: the
 * abbreviation used to fall back to the key, and a service with neither would
 * draw as a blank tile. So the fallback has a second step — the name's own first
 * letter — and it lives here rather than in whichever file happened to need it
 * first, because the store builds services from a form and js/sync.js builds the
 * same services from a database row, and the two disagreeing would mean a
 * service that renders one way on the station that added it and another way
 * everywhere else.
 *
 * Both functions are total: they answer for null, for whitespace, and for the
 * two-letter paste into a one-letter box. This file imports nothing.
 */

/* A hotkey as it is stored: one upper-case character, or the empty string for a
 * service that has none.
 *
 * Empty string rather than null, because this is what the board compares
 * keystrokes against and `''` is falsy in the one line that matters —
 * `shortcutMap` skips it, so a keyless service is simply not on the keyboard.
 * The database column is nullable and takes null; see `serviceRow`, which is the
 * one place the two spellings meet.
 */
export function cleanShortcut(value) {
  return String(value == null ? '' : value).trim().toUpperCase().charAt(0);
}

/* What gets drawn on the square, and it is never empty.
 *
 * Three answers in order:
 *
 *   abbrev     what somebody set, up to two characters, exactly as typed —
 *              "GX" and "Gx" are both a deliberate choice, so this one is not
 *              upper-cased.
 *   shortcut   the key, where there is one. The desk already reads that letter
 *              as this service; the square agreeing with the keyboard is worth
 *              more than a letter chosen from the name.
 *   name       the first character of it, upper-cased — the answer for a
 *              service with no key at all.
 *
 * The last resort is `?` rather than a blank, and it is reachable: a row whose
 * name is empty should draw as something visibly wrong rather than as an empty
 * tile nobody can click on with confidence.
 */
export function serviceAbbrev(abbrev, name, shortcut) {
  var set = String(abbrev == null ? '' : abbrev).trim().slice(0, 2);
  if (set) return set;
  var key = cleanShortcut(shortcut);
  if (key) return key;
  return String(name == null ? '' : name).trim().charAt(0).toUpperCase() || '?';
}
